import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrphanedUploadsReport } from "../../../store/thunks/reportsThunk";
import Table from "../../../applicationUi/components/Table";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const OrphanedUploadsReport = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const itemsPerPage = 25;

  useEffect(() => {
    console.log(`[Orphaned Uploads Report] Component mounted, fetching report...`);
    handleGenerateReport();
  }, []);

  const handleGenerateReport = () => {
    setLoading(true);
    dispatch(fetchOrphanedUploadsReport())
      .unwrap()
      .then((data) => {
        console.log(`[Orphaned Uploads Report] Report data received:`, data);
        setReportData(data);
        setLoading(false);

        if (data.stats.orphanedCount > 0) {
          toast.warning(`Found ${data.stats.orphanedCount} orphaned upload records!`);
        } else {
          toast.success(`No orphaned uploads found. All records match physical files.`);
        }
      })
      .catch((error) => {
        console.error(`[Orphaned Uploads Report] Error fetching report:`, error);
        setLoading(false);
        toast.error("Failed to generate orphaned uploads report");
      });
  };

  const columns = [
    { header: "File Name", accessor: "fileName" },
    { header: "Client Name", accessor: "clientName" },
    { header: "Folder Path", accessor: "folderPath" },
    { header: "Upload Date", accessor: "uploadDateFormatted" },
    { header: "Uploaded By", accessor: "uploadedBy" },
    { header: "Password Protected", accessor: "passwordProtected" },
    { header: "Expected Path", accessor: "expectedPath" },
  ];

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDownloadExcel = () => {
    if (!reportData || !reportData.orphanedUploads) {
      toast.error("No data to download");
      return;
    }

    const excelData = reportData.orphanedUploads.map((upload) => ({
      "File Name": upload.fileName,
      "Client Name": upload.clientName,
      "Client ID": upload.clientId,
      "File Path": upload.filePath,
      "Upload Date": new Date(upload.uploadDate).toLocaleDateString(),
      "Uploaded By": upload.uploadedBy,
      "Password Protected": upload.isPasswordProtected ? "Yes" : "No",
      "Expected Physical Path": upload.expectedPath,
      "Database Record ID": upload._id,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "OrphanedUploads");

    // Add a summary sheet
    const summaryData = [
      { Metric: "Total Database Records", Value: reportData.stats.totalRecords },
      { Metric: "Orphaned Records", Value: reportData.stats.orphanedCount },
      { Metric: "Valid Records", Value: reportData.stats.validCount },
      { Metric: "Clients Affected", Value: reportData.stats.clientsAffected },
      { Metric: "Report Generated", Value: new Date(reportData.generatedAt).toLocaleString() },
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    const workbookOutput = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([workbookOutput], {
      type: "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `OrphanedUploads_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success("Report downloaded successfully");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Scanning database and file system...</p>
          <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No report data available</p>
        <button
          onClick={handleGenerateReport}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Generate Report
        </button>
      </div>
    );
  }

  // Prepare data for table display
  const tableData = reportData.orphanedUploads.map((upload) => ({
    ...upload,
    folderPath: upload.filePath.split("/").slice(0, -1).join("/") || "root",
    uploadDateFormatted: new Date(upload.uploadDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    passwordProtected: upload.isPasswordProtected ? "Yes" : "No",
  }));

  const totalItems = tableData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = tableData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-sm shadow border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">Total Records</div>
          <div className="text-2xl font-bold text-gray-900">{reportData.stats.totalRecords}</div>
        </div>
        <div className="bg-white p-4 rounded-sm shadow border-l-4 border-red-500">
          <div className="text-sm text-gray-600">Orphaned Files</div>
          <div className="text-2xl font-bold text-red-600">{reportData.stats.orphanedCount}</div>
        </div>
        <div className="bg-white p-4 rounded-sm shadow border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Valid Files</div>
          <div className="text-2xl font-bold text-green-600">{reportData.stats.validCount}</div>
        </div>
        <div className="bg-white p-4 rounded-sm shadow border-l-4 border-yellow-500">
          <div className="text-sm text-gray-600">Clients Affected</div>
          <div className="text-2xl font-bold text-yellow-600">{reportData.stats.clientsAffected}</div>
        </div>
      </div>

      {/* Warning Banner */}
      {reportData.stats.orphanedCount > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Orphaned Upload Records Found
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  These database records reference files that no longer exist on the server.
                  This typically happens when files are deleted from the file system but the database records remain.
                </p>
                <p className="mt-2 font-semibold">
                  Recommended Action: Review the list below and consider deleting these orphaned database records.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow-sm rounded-sm">
        {reportData.stats.orphanedCount > 0 ? (
          <Table
            columns={columns}
            data={paginatedData}
            title="Orphaned Upload Records"
            description={`Found ${reportData.stats.orphanedCount} database records with missing physical files. Generated ${new Date(reportData.generatedAt).toLocaleString()}`}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={totalItems}
            onPageChange={handlePageChange}
            actions={[
              {
                label: "Download as Excel",
                onClick: handleDownloadExcel,
              },
              {
                label: "Refresh Report",
                onClick: handleGenerateReport,
              },
            ]}
          />
        ) : (
          <div className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Orphaned Files Found</h3>
            <p className="text-gray-500 mb-4">
              All {reportData.stats.totalRecords} upload records in the database have corresponding physical files on the server.
            </p>
            <button
              onClick={handleGenerateReport}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Refresh Report
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrphanedUploadsReport;
